import { useEffect, useMemo, useRef, useState } from "react";
import { useVehicleCatalog } from "../lib/api";
import { cn } from "../lib/utils";
import { filterVehicles } from "../lib/vehicles";
import { Icon } from "./msd/Icon";
import { Field } from "./msd/ui";

// Searchable vehicle-model dropdown for booking. `value` is the picker's state
// (see EMPTY_VEHICLE in lib/vehicles.js); `inputClassName` lets each booking
// screen keep its own field styling; `hint` is the line under the model box.
export default function VehiclePicker({ value, onChange, inputClassName, hint, testid = "vehicle-picker" }) {
  const { data, isLoading, isError } = useVehicleCatalog();
  const rows = useMemo(() => data?.rows || [], [data]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  const selected = rows.find((r) => r.id === Number(value.catalogId)) || null;
  const matches = useMemo(() => filterVehicles(rows, query), [rows, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const patch = (p) => onChange({ ...value, ...p });
  const choose = (row) => {
    patch({ catalogId: row.id, notListed: false, typed: "" });
    setOpen(false);
    setQuery("");
  };
  const chooseNotListed = () => {
    patch({ catalogId: null, notListed: true });
    setOpen(false);
    setQuery("");
  };

  // A failed or empty catalog must not strand the advisor: fall straight to
  // free text, as if "Not listed" were chosen.
  const catalogUnavailable = !isLoading && (isError || rows.length === 0);
  const showTyped = value.notListed || catalogUnavailable;

  return (
    <div className="flex flex-col gap-[18px]" data-testid={testid}>
      <div className="flex flex-col gap-[7px]">
        {!catalogUnavailable && (
          <div className="relative" ref={boxRef}>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={open}
              data-testid={`${testid}-button`}
              className={cn(inputClassName, "flex w-full items-center gap-3 text-left")}
            >
              {selected ? (
                <>
                  <MiniThumb row={selected} />
                  <span className="flex-1 truncate">{selected.name}</span>
                </>
              ) : (
                <span className={cn("flex-1 truncate", value.notListed ? "text-msd-ink" : "text-msd-faint")}>
                  {isLoading ? "Loading vehicles…" : value.notListed ? "Not listed" : "Search and select a vehicle…"}
                </span>
              )}
              <Icon name="down" size={16} stroke={2} className="shrink-0 text-msd-muted" />
            </button>

            {open && (
              <div className="absolute z-30 mt-[6px] w-full overflow-hidden rounded-[14px] bg-msd-surface shadow-msd-pop">
                <div className="flex items-center gap-2 px-[14px] text-msd-muted shadow-[0_1px_0_var(--line)]">
                  <Icon name="search" size={16} className="shrink-0" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type to search"
                    data-testid={`${testid}-search`}
                    className="h-11 w-full border-0 bg-transparent text-[15px] text-msd-ink outline-none placeholder:text-msd-faint"
                  />
                </div>
                <ul role="listbox" className="m-0 max-h-64 list-none overflow-y-auto p-0 py-1">
                  {matches.map((r) => (
                    <li key={r.id} role="option" aria-selected={r.id === selected?.id}>
                      <button
                        type="button"
                        onClick={() => choose(r)}
                        data-testid={`${testid}-option`}
                        className={cn(
                          "flex w-full items-center gap-3 border-0 px-[14px] py-[9px] text-left text-[15px] text-msd-ink hover:bg-msd-soft",
                          r.id === selected?.id ? "bg-[#F2F7FF] [:root[data-theme=dark]_&]:bg-[#14244A]" : "bg-transparent",
                        )}
                      >
                        <MiniThumb row={r} />
                        {r.name}
                      </button>
                    </li>
                  ))}
                  {matches.length === 0 && <li className="px-[14px] py-3 text-[13px] text-msd-muted">No match. Try "Not listed" below.</li>}
                </ul>
                <button
                  type="button"
                  onClick={chooseNotListed}
                  data-testid={`${testid}-not-listed`}
                  className="w-full border-0 bg-transparent px-[14px] py-3 text-left text-[14px] font-semibold text-[#2667B5] shadow-[0_-1px_0_var(--line)] hover:bg-msd-soft"
                >
                  Not listed
                </button>
              </div>
            )}
          </div>
        )}

        {showTyped && (
          <input
            className={inputClassName}
            value={value.typed}
            onChange={(e) => patch({ typed: e.target.value, notListed: true, catalogId: null })}
            placeholder="Vehicle model, e.g. Grand Vitara"
            data-testid={`${testid}-typed`}
          />
        )}

        {hint ? <span className="text-[12.5px] text-msd-muted">{hint}</span> : null}
      </div>

      <div className="grid grid-cols-3 gap-3 max-[640px]:grid-cols-1">
        <Field label="Variant">
          <input
            className={inputClassName}
            value={value.variant}
            onChange={(e) => patch({ variant: e.target.value })}
            placeholder="ZXi"
            aria-label="Variant"
            data-testid={`${testid}-variant`}
          />
        </Field>
        <Field label="Fuel">
          <input
            className={inputClassName}
            value={value.fuel}
            onChange={(e) => patch({ fuel: e.target.value })}
            placeholder="Petrol"
            aria-label="Fuel"
            data-testid={`${testid}-fuel`}
          />
        </Field>
        <Field label="Year">
          <input
            className={inputClassName}
            value={value.year}
            onChange={(e) => patch({ year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            placeholder="2023"
            aria-label="Year"
            inputMode="numeric"
            data-testid={`${testid}-year`}
          />
        </Field>
      </div>
    </div>
  );
}

function MiniThumb({ row }) {
  return (
    <span className="flex h-7 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-msd-soft text-msd-faint">
      {row.thumb_url ? <img src={row.thumb_url} alt="" className="h-full w-full object-cover" /> : <Icon name="car" size={15} />}
    </span>
  );
}
